
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {DropSessionResponse} from './DropSessionResponse';

/**
 * DropSessionRequest struct
 */
class DropSessionRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {UUID=} dropSessionId
   * @param {number=} dropSessionLogin
   * @constructor
   */
  constructor (parent = new Request(), dropSessionId = null, dropSessionLogin = null) {
    super()
    super.copy(parent)
    this.DropSessionId = dropSessionId
    this.DropSessionLogin = dropSessionLogin
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DropSessionRequest}
   * @param {!DropSessionRequest} other Other struct
   * @returns {!DropSessionRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.DropSessionId != null) {
      this.DropSessionId = new UUID(other.DropSessionId)
    } else {
      this.DropSessionId = null
    }
    if (other.DropSessionLogin != null) {
      this.DropSessionLogin = UInt64.fromNumber(other.DropSessionLogin)
    } else {
      this.DropSessionLogin = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DropSessionRequest}
   * @returns {!DropSessionRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DropSessionRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DropSessionRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DropSessionRequest}
   * @param {!DropSessionRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DropSessionRequest)) {
      throw new TypeError('Instance of DropSessionRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DropSessionRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      DropSessionId: ((this.DropSessionId != null) ? this.DropSessionId.toString() : null),
      DropSessionLogin: ((this.DropSessionLogin != null) ? this.DropSessionLogin.toNumber() : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DropSessionRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DropSessionRequest} other Object value
   * @returns {!DropSessionRequest} Created struct
   */
  static fromObject (other) {
    return new DropSessionRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DropSessionRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DropSessionRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DropSessionRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6013
  }
}

export { DropSessionRequest };

/**
 * Fast Binary Encoding DropSessionRequest field model
 */
class FieldModelDropSessionRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.DropSessionId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.DropSessionLogin = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.DropSessionId.fbeOffset + this.DropSessionId.fbeSize), buffer, this.DropSessionId.fbeOffset + this.DropSessionId.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelDropSessionRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelDropSessionRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDropSessionRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.DropSessionId.fbeSize + this.DropSessionLogin.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDropSessionRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.DropSessionId.fbeExtra + this.DropSessionLogin.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDropSessionRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDropSessionRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDropSessionRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6013
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDropSessionRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDropSessionRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DropSessionId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DropSessionId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DropSessionId.fbeSize

    if ((fbeCurrentSize + this.DropSessionLogin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DropSessionLogin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DropSessionLogin.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDropSessionRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDropSessionRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDropSessionRequest}
   * @param {!DropSessionRequest} fbeValue Default value, defaults is new DropSessionRequest()
   * @returns {!DropSessionRequest} DropSessionRequest value
   */
  get (fbeValue = new DropSessionRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDropSessionRequest}
   * @param {!DropSessionRequest} fbeValue DropSessionRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DropSessionId.fbeSize) <= fbeStructSize) {
      fbeValue.DropSessionId = this.DropSessionId.get()
    } else {
      fbeValue.DropSessionId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DropSessionId.fbeSize

    if ((fbeCurrentSize + this.DropSessionLogin.fbeSize) <= fbeStructSize) {
      fbeValue.DropSessionLogin = this.DropSessionLogin.get()
    } else {
      fbeValue.DropSessionLogin = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DropSessionLogin.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDropSessionRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDropSessionRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDropSessionRequest}
   * @param {!DropSessionRequest} fbeValue DropSessionRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDropSessionRequest}
   * @param {DropSessionRequest} fbeValue DropSessionRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.DropSessionId.set(fbeValue.DropSessionId)
    this.DropSessionLogin.set(fbeValue.DropSessionLogin)
  }
}

export { FieldModelDropSessionRequest };

/**
 * Fast Binary Encoding DropSessionRequest model
 */
class DropSessionRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDropSessionRequest(this.buffer, 4)
  }

  /**
   * Get the DropSessionRequest model
   * @this {!DropSessionRequestModel}
   * @returns {!FieldModelDropSessionRequest} model DropSessionRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DropSessionRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DropSessionRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DropSessionRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DropSessionRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDropSessionRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DropSessionRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DropSessionRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DropSessionRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DropSessionRequestModel}
   * @param {!DropSessionRequest} value DropSessionRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DropSessionRequestModel}
   * @param {!DropSessionRequest} value DropSessionRequest value, defaults is new DropSessionRequest()
   * @return {!object} Deserialized DropSessionRequest value and its size
   */
  deserialize (value = new DropSessionRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DropSessionRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DropSessionRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DropSessionRequestModel}
   * @param {!number} prev Previous DropSessionRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DropSessionRequestModel };
DropSessionRequest.__has_response = true;
DropSessionRequest.__response_class = DropSessionResponse;
